import React, { useEffect, useRef, useState } from "react";
import { manualApi, won } from "./LaonaProjects";

/* =========================================================
   펌웨어 개발의뢰 — 내 프로젝트(수동)의 [펌웨어 개발의뢰] 팝업 (백엔드 app/laona_fw.py).
   라오나 에디터의 [기능정의]에서 쓰고 [완료]로 저장한 기능정의서 + 모듈 목록 + 연결 리스트를 묶어 의뢰한다.
   비용은 [주문하기]에서 [펌웨어 빌드 포함]에 체크하면 부품과 함께 한 주문으로 낸다. 입금이 확인되면
   라오나빌드서버가 의뢰를 가져가고, 개발 · 빌드는 운영자가 직접 한다.
   ========================================================= */

export const FW_STATUS_TONE = { REQUESTED: "wait", ORDERED: "on", RECEIVED: "on", IN_PROGRESS: "on", DONE: "done", CANCELLED: "" };

/* 비용 · 범위 안내 — 의뢰 팝업과 주문 팝업이 함께 쓴다 */
export function FwTerms({ fee }) {
  return (
    <ul className="lb-fw__terms">
      <li>
        펌웨어 개발·빌드 비용은 <b>기본 {won(fee)}</b>이며, 작업량에 따라 추가 비용이 최대 10만원까지 발생할 수 있습니다.
        다만 일반적으로 추가 비용은 발생하지 않습니다.
      </li>
      <li>펌웨어는 바이너리를 포함해 코드 전체를 제공합니다.</li>
      <li>기능정의서에 적힌 기능만 구현됩니다. 기능정의서에 상세히 기재될수록 펌웨어 동작 정확도가 올라갑니다.</li>
      <li>수정을 요청하면 추가 비용이 발생합니다.</li>
    </ul>
  );
}

export default function FirmwareDialog({ project, onClose, onSaved }) {
  const ref = useRef(null);
  const [state, setState] = useState({ status: "loading", d: null, error: "" });
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);

  useEffect(() => {
    let alive = true;
    manualApi(`/${project.id}/firmware`)
      .then((d) => alive && setState({ status: "ready", d, error: "" }))
      .catch((err) => alive && setState({ status: "error", d: null, error: err.message }));
    return () => { alive = false; };
  }, [project.id]);

  const d = state.d;
  const req = d?.request;
  const editable = !req || ["REQUESTED", "ORDERED", "DONE", "CANCELLED"].includes(req.status);
  const again = req && ["DONE", "CANCELLED"].includes(req.status); // 끝난 의뢰 — 새로 의뢰한다
  const changed = req && !again && d && req.func_spec !== d.func_spec;

  async function submit(e) {
    e.preventDefault();
    if (!agree) { setError("안내 내용을 확인하고 동의해 주세요."); return; }
    setBusy(true);
    setError("");
    try {
      const r = await manualApi(`/${project.id}/firmware`, { method: "POST" });
      setState((s) => ({ ...s, d: { ...s.d, request: r } }));
      await onSaved();
      setAgree(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog ref={ref} className="lb-dialog lb-dialog--fw" aria-labelledby="lb-fw-title" onClose={onClose}
            onClick={(e) => { if (e.target === ref.current && !busy) ref.current.close(); }}>
      <form className="lb-dialog__box" onSubmit={submit}>
        <div className="lb-dialog__head">
          <h2 id="lb-fw-title">펌웨어 개발의뢰</h2>
          <button type="button" className="lb-dialog__x" aria-label="닫기" onClick={() => ref.current.close()}>×</button>
        </div>
        <p className="lb-dialog__lead">
          <b>{project.title}</b>의 기능정의서와 모듈 · 연결 리스트로 펌웨어 개발 · 빌드를 의뢰합니다.
        </p>

        {state.status === "loading" && <p className="lb-empty">의뢰 내용을 불러오는 중입니다…</p>}
        {state.status === "error" && <p className="lb-error">{state.error}</p>}
        {d && (
          <>
            {req && (
              <div className="lb-fw__state">
                <span className={`lb-tag lb-tag--st lb-tag--${FW_STATUS_TONE[req.status] || ""}`}>{req.label}</span>
                <span>의뢰번호 <b>{req.request_no}</b></span>
                <span>
                  {req.status === "REQUESTED" && "[주문하기]에서 ‘펌웨어 빌드 포함’에 체크해 주문하면 개발이 진행됩니다."}
                  {req.status === "ORDERED" && `주문 #${req.order_no}에 포함되었습니다. 입금이 확인되면 개발을 시작합니다.`}
                  {["RECEIVED", "IN_PROGRESS"].includes(req.status) && "개발이 진행 중입니다. 바꿀 내용은 문의로 알려 주세요."}
                  {req.status === "DONE" && "개발이 끝난 의뢰입니다."}
                  {req.status === "CANCELLED" && "취소된 의뢰입니다."}
                </span>
              </div>
            )}
            <div className="lb-fw__sum">
              <span>모듈 <b>{d.modules.length}개</b>{d.modules.length > 0 && ` — ${d.modules.map((m) => m.key).join(", ")}`}</span>
              <span>연결 <b>{d.connections}개</b></span>
            </div>
            <div className="lb-fw__spec">
              <div className="lb-fw__spech">기능정의서{changed && <em> — 의뢰한 뒤 고친 내용이 있습니다. [의뢰 내용 고치기]로 반영하세요.</em>}</div>
              <pre>{d.func_spec || "기능정의서가 없습니다. 라오나 에디터에서 [기능정의]를 쓰고 [완료]를 눌러 주세요."}</pre>
            </div>
            <FwTerms fee={d.fee} />
            {editable && (
              <label className="lb-check lb-fw__agree">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} disabled={busy} />
                <span>위 내용을 확인했으며 펌웨어 개발을 의뢰합니다.</span>
              </label>
            )}
          </>
        )}
        {error && <p className="lb-error">{error}</p>}
        <div className="lb-order__acts">
          <button type="button" className="lb-btn" onClick={() => ref.current.close()} disabled={busy}>닫기</button>
          {d && editable && (
            <button type="submit" className="lb-btn lb-btn--primary" disabled={busy || !d.func_spec || !agree}>
              {busy ? "보내는 중…" : req && !again ? "의뢰 내용 고치기" : "의뢰하기"}
            </button>
          )}
        </div>
      </form>
    </dialog>
  );
}
